import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import TableHeader from './components/TableHeader';
import PatientTableRow from './components/PatientTableRow';
import PatientCard from './components/PatientCard';
import BulkActionsBar from './components/BulkActionsBar';
import EmptyState from './components/EmptyState';
import { supabase } from '../../lib/supabase';
import { useAuth } from "../../contexts/AuthContext";
import CreatePatientModal from './components/CreatePatientModal';
import CreateCaseModal from '../cases-list/components/CreateCaseModal';
import { formatPatientDisplayName } from '../../lib/patientIdentity';
import { isPatientMarkedInactiveStatus } from '../../lib/patientStatus';
import { activeCaseCountByPatientId } from '../../lib/activeCaseRules';
import { getCaseAssistantPath, isAdminAssistantRole } from '../../lib/dentistSubmissionWizard';

const PatientsListPage = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { userProfile: authUser, profileLoading } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    caseStatus: 'all',
    location: '',
    dateFrom: '',
    dateTo: '',
    dentist: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [caseModalPatient, setCaseModalPatient] = useState(null);
  const [caseModalDentist, setCaseModalDentist] = useState(null);

  // Vue globale : administrateur + agent administratif
  const canManageAllPatients = isAdminAssistantRole(authUser?.roleTypes);
  const isAdministrator = authUser?.roleTypes?.includes('administrator');

  // Load patients from Supabase
  useEffect(() => {
    if (authUser?.id) {
      fetchPatients();
    }
  }, [authUser?.id]);

  const handlePatientCreated = (newPatient) => {
    fetchPatients();
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      const canManageAll = isAdminAssistantRole(authUser?.roleTypes);

      let query = supabase
        .from('patients')
        .select(`
          *,
          user_profiles:user_profile_id (
            id,
            full_name,
            email,
            company_name
          )
        `)
        .order('created_at', { ascending: false });

      if (!canManageAll) {
        query = query.eq('user_profile_id', authUser?.id);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const patientRows = data || [];
      const patientDbIds = patientRows.map((p) => p.id).filter(Boolean);

      let activeCountByPatientId = new Map();
      if (patientDbIds.length > 0) {
        let casesQuery = supabase
          .from('cases')
          .select('patient_id, status, current_phase')
          .in('patient_id', patientDbIds);

        if (!canManageAll) {
          casesQuery = casesQuery.eq('user_profile_id', authUser?.id);
        }

        const { data: caseRows, error: casesError } = await casesQuery;
        if (casesError) throw casesError;
        activeCountByPatientId = activeCaseCountByPatientId(caseRows);
      }

      const transformedPatients = patientRows.map(patient => ({
        id: patient.patient_code,
        name: formatPatientDisplayName(patient),
        first_name: patient.first_name,
        last_name: patient.last_name,
        gender: patient.gender,
        date_birth: patient.date_birth,
        initials: patient.initials,
        email: patient.email,
        phone: patient.phone,
        registrationDate: new Date(patient.registration_date).toLocaleDateString('fr-FR'),
        registration_date: patient.registration_date,
        activeCases: activeCountByPatientId.get(patient.id) ?? 0,
        lastVisit: patient.last_visit ? new Date(patient.last_visit).toLocaleDateString('fr-FR') : 'N/A',
        status: patient.status,
        location: patient.location,
        dbId: patient.id,
        user_profiles: patient.user_profiles,
        dentist_client: patient.user_profiles?.company_name || patient.user_profiles?.full_name || 'N/A'
      }));

      setPatients(transformedPatients || []);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients?.filter((patient) => {
    // Search filter
    const matchesSearch = 
      patient?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      patient?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      patient?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      patient?.phone?.includes(searchTerm);

    // Statut : « Actif » = tout sauf marqués inactifs (tolère variantes en base)
    const matchesStatus =
      filters?.status === 'all' ||
      (filters?.status === 'active' &&
        !isPatientMarkedInactiveStatus(patient?.status)) ||
      (filters?.status === 'inactive' &&
        isPatientMarkedInactiveStatus(patient?.status));
    
    // Case status filter
    const matchesCaseStatus = 
      filters?.caseStatus === 'all' ||
      (filters?.caseStatus === 'with-active' && patient?.activeCases > 0) ||
      (filters?.caseStatus === 'without-active' && patient?.activeCases === 0);

    // Location filter - now supports partial matching
    const matchesLocation = 
      !filters?.location || 
      filters?.location === '' ||
      patient?.location?.toLowerCase()?.includes(filters?.location?.toLowerCase());

    const matchesDentist = 
      !canManageAllPatients || 
      filters?.dentist === 'all' || 
      !filters?.dentist ||
      patient?.user_profiles?.id === filters?.dentist;

    // Date range filter
    let matchesDateRange = true;
    if (filters?.dateFrom || filters?.dateTo) {
      const registrationDate = new Date(patient?.registration_date || patient?.registrationDate);
      
      if (filters?.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        matchesDateRange = matchesDateRange && registrationDate >= fromDate;
      }
      
      if (filters?.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        matchesDateRange = matchesDateRange && registrationDate <= toDate;
      }
    }

    return matchesSearch && matchesStatus && matchesCaseStatus && matchesLocation && matchesDentist && matchesDateRange;
  });

  const sortedPatients = [...filteredPatients]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];

    if (sortConfig?.key === 'activeCases') {
      return sortConfig?.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === 'string') {
      return sortConfig?.direction === 'asc'
        ? aValue?.localeCompare(bValue)
        : bValue?.localeCompare(aValue);
    }

    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectPatient = (patientId, isSelected) => {
    setSelectedPatients((prev) =>
      isSelected ? [...prev, patientId] : prev?.filter((id) => id !== patientId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedPatients(isSelected ? sortedPatients?.map((p) => p?.id) : []);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      caseStatus: 'all',
      location: '',
      dateFrom: '',
      dateTo: '',
      dentist: 'all'
    });
    setSearchTerm('');
  };

// In PatientsListPage.jsx - Update the handleDeletePatients function

const handleDeletePatients = async () => {
  try {
    setError(null);

    console.log('Selected patients:', selectedPatients);
    console.log('All patients:', patients);

    // Get the dbIds (UUIDs) of selected patients
    const patientsToDelete = patients.filter(p => 
      selectedPatients.includes(p.id)
    );
    
    console.log('Patients to delete:', patientsToDelete);
    
    const dbIds = patientsToDelete.map(p => p.dbId).filter(Boolean);

    console.log('Deleting patients with dbIds:', dbIds);

    if (dbIds.length === 0) {
      throw new Error('Aucun patient à supprimer - IDs non trouvés');
    }

    // Delete from Supabase
    const { data, error: deleteError } = await supabase
      .from('patients')
      .delete()
      .in('id',dbIds)
      .select();

    if (deleteError) throw deleteError;

    console.log('Deleted successfully:', data);

    // Clear selection
    setSelectedPatients([]);

    // Refresh the patient list
    await fetchPatients();

  } catch (err) {
    console.error('Error deleting patients:', err);
    setError(`Erreur lors de la suppression: ${err.message}`);
  }
};


  const handleExport = () => {
    console.log('Exporting patients:', selectedPatients);
  };

  const handleCommunication = () => {
    console.log('Opening communication for patients:', selectedPatients);
  };

  const hasActiveFilters = 
    searchTerm !== '' ||
    Object.entries(filters)?.some(([key, value]) => {
      if (key === 'location') return value !== '';
      return value !== 'all' && value !== '';
    });

  const breadcrumbItems = canManageAllPatients
    ? [
        { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
        { label: 'Patients', path: '/patients-list', icon: 'Users' },
      ]
    : [
        { label: 'Espace dentiste', path: '/dentiste', icon: 'LayoutGrid' },
        { label: 'Patients', path: '/patients-list', icon: 'Users' },
      ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Breadcrumb items={breadcrumbItems} />
          <div className="px-6 py-6 flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement des patients...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Breadcrumb items={breadcrumbItems} />
          <div className="px-6 py-6">
            <div className="bg-destructive/10 border border-destructive rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-destructive mb-2">
                Erreur de chargement
              </h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchPatients} variant="outline">
                Réessayer
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Gestion des Patients
              </h1>
              <p className="text-sm text-muted-foreground">
                {canManageAllPatients
                  ? 'Gérez et suivez tous les dossiers patients de votre clinique orthodontique'
                  : 'Liste de vos dossiers patients'}
              </p>
            </div>
            <Button
              variant="default"
              onClick={() => setIsModalOpen(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Nouveau Patient
            </Button>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('table')}
                  title="Vue tableau"
                >
                  <Icon name="Table" size={20} />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  title="Vue grille"
                >
                  <Icon name="Grid" size={20} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchPatients}
                  title="Actualiser"
                >
                  <Icon name="RefreshCw" size={20} />
                </Button>
              </div>
            </div>

            <FilterPanel
              filters={filters}
              onFilterChange={setFilters}
              onClearFilters={handleClearFilters}
              resultCount={sortedPatients?.length}
            />
          </div>

          {sortedPatients?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg">
              <EmptyState
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            </div>
          ) : viewMode === 'table' ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <TableHeader
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    allSelected={selectedPatients?.length === sortedPatients?.length}
                    onSelectAll={handleSelectAll}
                    isAdmin={canManageAllPatients}
                  />
                  <tbody>
                    {sortedPatients?.map((patient) => (
                      <PatientTableRow
                        key={patient?.id}
                        patient={patient}
                        isSelected={selectedPatients?.includes(patient?.id)}
                        onSelect={handleSelectPatient}
                        isAdmin={canManageAllPatients}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedPatients?.map((patient) => (
                <PatientCard
                  key={patient?.id}
                  patient={patient}
                  isSelected={selectedPatients?.includes(patient?.id)}
                  onSelect={handleSelectPatient}
                />
              ))}
            </div>
          )}
        </div>
      </main>
     {isAdministrator && (
       <BulkActionsBar
         selectedCount={selectedPatients?.length}
         onDelete={handleDeletePatients}
         onClearSelection={() => setSelectedPatients([])}
       />
     )}
      <CreatePatientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPatientCreated={handlePatientCreated}
        onCreateCaseForPatient={(patientRow, dentistProfile) => {
          setCaseModalPatient(patientRow);
          setCaseModalDentist(dentistProfile);
        }}
        currentUserId={authUser?.id}
      />
      <CreateCaseModal
        isOpen={!!caseModalPatient}
        onClose={() => {
          setCaseModalPatient(null);
          setCaseModalDentist(null);
        }}
        patient={caseModalPatient}
        preselectedDentist={caseModalDentist}
        currentUserId={authUser?.id}
        currentUserRole={authUser?.roleTypes}
        onCaseCreated={(caseData) => {
          setCaseModalPatient(null);
          setCaseModalDentist(null);
          navigate(getCaseAssistantPath(caseData.id));
        }}
      />
    </div>
  );
};

export default PatientsListPage;